import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { FiCreditCard, FiEdit2, FiPlus, FiSmartphone } from 'react-icons/fi';
import { pageTransition, staggerContainer, staggerItem, tapScale, tapScaleTransition } from '../helpers/animations';
import { api, normalizeToken, tokenPath } from '../helpers';
import { API_BASE_URL } from '../helpers/config';
import { INDIAN_BANKS } from '../helpers/indianBanks';
import SearchableSelect from '../components/SearchableSelect';
import { BankAccountsSkeleton } from '../components/skeletons';

const EMPTY_FORM = {
  bank_id: '',
  type: 'bank',
  bank_name: '',
  account_no: '',
  account_holder: '',
  ifsc_code: '',
  upi_id: '',
  name: '',
};

function maskAccount(value) {
  const digits = String(value || '').replace(/\s/g, '');
  if (digits.length <= 4) return digits || '—';
  return `${'•'.repeat(Math.min(digits.length - 4, 8))}${digits.slice(-4)}`;
}

function normalizeAccounts(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      const type = String(item?.type || '').toLowerCase() === 'upi' ? 'upi' : 'bank';
      const accountNo = String(item?.account_no ?? item?.account_number ?? '').trim();
      const holder = String(item?.account_holder ?? item?.name ?? '').trim();
      const upiId = String(item?.upi_id ?? (type === 'upi' ? accountNo : '')).trim();
      const name = String(item?.name ?? holder).trim();

      return {
        bank_id: String(item?.bank_id ?? item?.id ?? `${type}-${index}`),
        type,
        bank_name: String(item?.bank_name || '').trim(),
        account_no: accountNo,
        account_holder: holder,
        ifsc_code: String(item?.ifsc_code || item?.ifsc || '').trim().toUpperCase(),
        upi_id: upiId,
        name,
      };
    })
    .filter((item) => (item.type === 'upi' ? item.upi_id || item.account_no : item.account_no || item.bank_name));
}

function apiErrorMessage(data, fallback) {
  if (typeof data?.error === 'string' && data.error) return data.error;
  if (typeof data?.msg === 'string' && data?.error) return data.msg;
  return fallback;
}

function BankAccounts() {
  const { token: tokenParam } = useParams();
  const token = normalizeToken(tokenParam);
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleSessionExpired = useCallback(() => {
    toast.error('Session expired. Please open your access link again.');
    navigate('/', { replace: true });
  }, [navigate]);

  const fetchAccounts = useCallback(async () => {
    if (!token) {
      handleSessionExpired();
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`${API_BASE_URL}/exchange/bank-list`, { token });
      if (data?.error === false) {
        setAccounts(normalizeAccounts(data?.data));
      } else if (data?.error === 'session expired') {
        handleSessionExpired();
      } else if (data?.error) {
        toast.error(apiErrorMessage(data, 'Failed to load bank accounts'));
      }
    } catch {
      // api helper toast
    } finally {
      setLoading(false);
    }
  }, [handleSessionExpired, token]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setEditingId('');
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditingId(item.bank_id);
    setShowForm(true);
    setErrors({});
    if (item.type === 'upi') {
      setForm({
        ...EMPTY_FORM,
        bank_id: item.bank_id,
        type: 'upi',
        upi_id: item.upi_id || item.account_no,
        name: item.name || item.account_holder,
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        bank_id: item.bank_id,
        type: 'bank',
        bank_name: item.bank_name,
        account_no: item.account_no,
        account_holder: item.account_holder,
        ifsc_code: item.ifsc_code,
      });
    }
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const setType = (type) => {
    setForm((prev) => ({
      ...EMPTY_FORM,
      bank_id: prev.bank_id,
      type,
    }));
    setErrors({});
  };

  const validate = () => {
    const next = {};
    if (form.type === 'upi') {
      if (!form.upi_id.trim()) next.upi_id = 'UPI ID is required';
      else if (!/^[\w.\-]{2,}@[a-zA-Z]{2,}$/.test(form.upi_id.trim())) {
        next.upi_id = 'Enter a valid UPI ID (e.g. name@upi)';
      }
      if (!form.name.trim()) next.name = 'Name is required';
    } else {
      if (!form.bank_name.trim()) next.bank_name = 'Please select a bank';
      if (!form.account_no.trim()) next.account_no = 'Account number is required';
      else if (!/^\d{9,18}$/.test(form.account_no.trim())) {
        next.account_no = 'Enter a valid account number (9–18 digits)';
      }
      if (!form.account_holder.trim()) next.account_holder = 'Account holder name is required';
      if (!form.ifsc_code.trim()) next.ifsc_code = 'IFSC is required';
      else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/i.test(form.ifsc_code.trim())) {
        next.ifsc_code = 'Enter a valid IFSC (e.g. SBIN0001234)';
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildPayload = () => {
    if (form.type === 'upi') {
      const payload = {
        token,
        type: 'upi',
        upi_id: form.upi_id.trim(),
        name: form.name.trim(),
        account_no: form.upi_id.trim(),
        account_holder: form.name.trim(),
      };
      if (editingId) payload.bank_id = editingId;
      return payload;
    }

    const payload = {
      token,
      type: 'bank',
      bank_name: form.bank_name.trim(),
      account_no: form.account_no.trim(),
      account_holder: form.account_holder.trim(),
      ifsc_code: form.ifsc_code.trim().toUpperCase(),
    };
    if (editingId) payload.bank_id = editingId;
    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting || !validate()) return;

    if (!token) {
      handleSessionExpired();
      return;
    }

    const endpoint = editingId ? 'edit-bank' : 'create-bank';
    setSubmitting(true);
    try {
      const { data } = await api.post(`${API_BASE_URL}/exchange/${endpoint}`, buildPayload());
      if (data?.error === false) {
        toast.success(data?.msg || (editingId ? 'Account updated successfully' : 'Account added successfully'));
        closeForm();
        await fetchAccounts();
      } else if (data?.error === 'session expired') {
        handleSessionExpired();
      } else if (data?.error) {
        toast.error(apiErrorMessage(data, editingId ? 'Failed to update account' : 'Failed to add account'));
      }
    } catch {
      // api helper toast
    } finally {
      setSubmitting(false);
    }
  };

  const isEditing = Boolean(editingId);

  return (
    <motion.div
      {...pageTransition}
      transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
    >
      <motion.h1 className="screen-title" variants={staggerItem} initial="initial" animate="animate">
        Bank accounts
      </motion.h1>
      <motion.p className="buy-merchant-back" variants={staggerItem} initial="initial" animate="animate">
        <Link to={tokenPath(token, 'account')}>Back to Account</Link>
      </motion.p>

      <motion.div
        className="card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.25 }}
      >
        <div className="bank-accounts-head">
          <div>
            <h2 className="card-title" style={{ margin: 0 }}>
              Saved accounts
            </h2>
            <p className="card-subtitle" style={{ marginTop: 4 }}>
              Bank and UPI details for withdrawals.
            </p>
          </div>
          <motion.button
            type="button"
            className="btn primary btn-sm bank-accounts-add-btn"
            onClick={() => {
              if (showForm && !isEditing) closeForm();
              else openCreate();
            }}
            whileTap={tapScale}
            transition={tapScaleTransition}
          >
            <FiPlus size={14} />
            {showForm && !isEditing ? 'Close' : 'Add'}
          </motion.button>
        </div>

        <AnimatePresence initial={false}>
          {showForm && (
            <motion.form
              className="form bank-account-form"
              onSubmit={handleSubmit}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: 'hidden' }}
            >
              <div className="bank-account-form-title">
                {isEditing ? 'Edit account' : 'Add account'}
              </div>

              <div className="form-group">
                <label>Type</label>
                <div className="auth-type-toggle">
                  <button
                    type="button"
                    className={`toggle-btn${form.type === 'bank' ? ' active' : ''}`}
                    onClick={() => setType('bank')}
                    disabled={isEditing}
                  >
                    Bank
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn${form.type === 'upi' ? ' active' : ''}`}
                    onClick={() => setType('upi')}
                    disabled={isEditing}
                  >
                    UPI
                  </button>
                </div>
              </div>

              {form.type === 'upi' ? (
                <>
                  <div className="form-group">
                    <label htmlFor="upi-id">UPI ID</label>
                    <input
                      id="upi-id"
                      type="text"
                      placeholder="name@upi"
                      value={form.upi_id}
                      onChange={(e) => setField('upi_id', e.target.value)}
                      className={errors.upi_id ? 'input-error' : ''}
                      autoComplete="off"
                    />
                    {errors.upi_id && <p className="field-error">{errors.upi_id}</p>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="upi-name">Name</label>
                    <input
                      id="upi-name"
                      type="text"
                      placeholder="Account holder name"
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                      className={errors.name ? 'input-error' : ''}
                      autoComplete="name"
                    />
                    {errors.name && <p className="field-error">{errors.name}</p>}
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="bank-name">Bank name</label>
                    <SearchableSelect
                      id="bank-name"
                      options={INDIAN_BANKS}
                      value={form.bank_name}
                      onChange={(val) => setField('bank_name', val)}
                      placeholder="Search Indian banks…"
                      error={!!errors.bank_name}
                    />
                    {errors.bank_name && <p className="field-error">{errors.bank_name}</p>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="account-no">Account number</label>
                    <input
                      id="account-no"
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter account number"
                      value={form.account_no}
                      onChange={(e) => setField('account_no', e.target.value.replace(/\D/g, '').slice(0, 18))}
                      className={errors.account_no ? 'input-error' : ''}
                      autoComplete="off"
                    />
                    {errors.account_no && <p className="field-error">{errors.account_no}</p>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="account-holder">Account holder</label>
                    <input
                      id="account-holder"
                      type="text"
                      placeholder="Name as per bank"
                      value={form.account_holder}
                      onChange={(e) => setField('account_holder', e.target.value)}
                      className={errors.account_holder ? 'input-error' : ''}
                      autoComplete="name"
                    />
                    {errors.account_holder && <p className="field-error">{errors.account_holder}</p>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="ifsc-code">IFSC</label>
                    <input
                      id="ifsc-code"
                      type="text"
                      placeholder="SBIN0001234"
                      value={form.ifsc_code}
                      onChange={(e) =>
                        setField('ifsc_code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11))
                      }
                      className={errors.ifsc_code ? 'input-error' : ''}
                      autoComplete="off"
                    />
                    {errors.ifsc_code && <p className="field-error">{errors.ifsc_code}</p>}
                  </div>
                </>
              )}

              <div className="bank-account-form-actions">
                {isEditing && (
                  <button type="button" className="btn ghost" onClick={closeForm} disabled={submitting}>
                    Cancel
                  </button>
                )}
                <motion.button
                  type="submit"
                  className="btn primary"
                  disabled={submitting}
                  whileTap={tapScale}
                  transition={tapScaleTransition}
                >
                  {submitting ? 'Saving…' : isEditing ? 'Update account' : 'Save account'}
                </motion.button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {loading ? (
          <BankAccountsSkeleton />
        ) : accounts.length === 0 ? (
          <div className="merchant-pick-empty">No bank or UPI accounts yet. Tap Add to create one.</div>
        ) : (
          <motion.div
            className="bank-account-list"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            {accounts.map((item) => (
              <motion.div key={item.bank_id} className="bank-account-item" variants={staggerItem}>
                <span className={`bank-account-icon bank-account-icon-${item.type}`} aria-hidden>
                  {item.type === 'upi' ? <FiSmartphone size={16} /> : <FiCreditCard size={16} />}
                </span>
                <div className="bank-account-body">
                  <div className="bank-account-title-row">
                    <span className="bank-account-title">
                      {item.type === 'upi'
                        ? item.name || item.account_holder || 'UPI account'
                        : item.bank_name || 'Bank account'}
                    </span>
                    <span className={`bank-account-type bank-account-type-${item.type}`}>
                      {item.type === 'upi' ? 'UPI' : 'Bank'}
                    </span>
                  </div>
                  {item.type === 'upi' ? (
                    <span className="bank-account-meta">{item.upi_id || item.account_no}</span>
                  ) : (
                    <>
                      <span className="bank-account-meta">
                        {maskAccount(item.account_no)}
                        {item.ifsc_code ? ` · ${item.ifsc_code}` : ''}
                      </span>
                      {item.account_holder ? (
                        <span className="bank-account-meta">{item.account_holder}</span>
                      ) : null}
                    </>
                  )}
                  <div className="bank-account-actions">
                    <button type="button" className="bank-account-action" onClick={() => openEdit(item)}>
                      <FiEdit2 size={13} />
                      Edit
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
}

export default BankAccounts;
